import { useTranslation } from 'react-i18next';
import type { LandingPillar } from '@dosfilos/domain';
import { LANDING } from '../landing-theme';
import { PILLAR_ICON } from '../pillar-icons';

/**
 * Los pilares del seminario: lo que lo define, en sus palabras. Es el diseño de
 * siempre, pero el contenido lo escribe cada seminario en Configuración →
 * Landing (antes era un texto fijo, igual para todos, y por eso se había quitado).
 */
export function PillarsSection({ pillars, gold }: { pillars: LandingPillar[]; gold: string }) {
  const { t } = useTranslation('sis');
  const { SERIF, SANS, TEXT_PRIMARY, TEXT_SECONDARY, BORDER, BG2 } = LANDING;
  // Tantas columnas como pilares (hasta 4): ninguna queda vacía.
  const cols = Math.min(Math.max(pillars.length, 1), 4);

  return (
    <section
      id="pilares"
      style={{
        background: BG2,
        borderTop: `1px solid ${BORDER}`,
        borderBottom: `1px solid ${BORDER}`,
      }}
    >
      <style>{`
        .pl-wrap { max-width: 1200px; margin: 0 auto; padding: 96px 64px; }
        .pl-head { margin-bottom: 56px; display: flex; align-items: flex-end; gap: 24px; }
        .pl-line { flex: 1; height: 1px; margin-bottom: 8px; background: linear-gradient(to right, ${BORDER}, transparent); }
        .pl-grid { display: grid; grid-template-columns: repeat(${cols}, minmax(0, 1fr)); gap: 24px; }
        .pl-card { padding: 36px 32px; border: 1px solid ${BORDER}; border-radius: 2px; }
        .pl-icon { width: 44px; height: 44px; border-radius: 2px; background: rgba(255,255,255,0.04); border: 1px solid ${BORDER}; display: flex; align-items: center; justify-content: center; margin-bottom: 20px; }
        @media (max-width: 1023px) {
          .pl-wrap { padding: 56px 24px; }
          .pl-grid { grid-template-columns: repeat(${Math.min(cols, 2)}, minmax(0, 1fr)); }
        }
        @media (max-width: 639px) {
          .pl-grid { grid-template-columns: minmax(0, 1fr); gap: 16px; }
          .pl-card { padding: 28px 24px; }
        }
      `}</style>
      <div className="pl-wrap">
        <div className="pl-head">
          <div>
            <p className="section-label">{t('publicLanding.pillars.label')}</p>
            <h2
              style={{
                fontFamily: SERIF,
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: 400,
                color: TEXT_PRIMARY,
                lineHeight: 1.15,
                margin: 0,
              }}
            >
              {t('publicLanding.pillars.title')}
            </h2>
          </div>
          <div className="pl-line" />
        </div>
        <div className="pl-grid">
          {pillars.map((p) => {
            const Icon = PILLAR_ICON[p.icon] ?? PILLAR_ICON.book;
            return (
              <article key={p.id} className="pl-card">
                <div className="pl-icon">
                  <Icon style={{ color: gold, fontSize: 22 }} />
                </div>
                <h3
                  style={{
                    fontFamily: SERIF,
                    fontSize: 22,
                    fontWeight: 500,
                    color: TEXT_PRIMARY,
                    margin: '0 0 12px',
                  }}
                >
                  {p.title}
                </h3>
                <p
                  style={{
                    fontFamily: SANS,
                    fontSize: 14,
                    lineHeight: 1.75,
                    color: TEXT_SECONDARY,
                    margin: 0,
                  }}
                >
                  {p.text}
                </p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
